export const formatTime = (date: Date, timeZone: string) =>
  new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    hour12: true,
    minute: "2-digit",
    timeZone,
  }).format(date);

export const formatClock = (date: Date, timeZone: string) =>
  new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    hour12: true,
    minute: "2-digit",
    second: "2-digit",
    timeZone,
  }).format(date);

export const formatDateLabel = (date: Date, timeZone: string) =>
  new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "long",
    timeZone,
    weekday: "long",
    year: "numeric",
  }).format(date);

export const shortTimeZone = (date: Date, timeZone: string) => {
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" })
      .formatToParts(date)
      .find((part) => part.type === "timeZoneName")?.value;
  } catch {
    return timeZone;
  }
};

const getTimeZoneNamePart = (date: Date, timeZone: string, timeZoneName: "longGeneric" | "shortOffset") => {
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName })
      .formatToParts(date)
      .find((part) => part.type === "timeZoneName")?.value;
  } catch {
    return null;
  }
};

export const formatTimeZoneName = (date: Date, timeZone: string) => getTimeZoneNamePart(date, timeZone, "longGeneric");

export const formatUtcOffset = (date: Date, timeZone: string) => getTimeZoneNamePart(date, timeZone, "shortOffset");

export const getBrowserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC";

export const formatCountdown = (ms: number) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }

  return `${seconds}s`;
};

export const getDatePartsInTimeZone = (date: Date, timeZone: string) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "numeric",
    timeZone,
    year: "numeric",
  }).formatToParts(date);

  const values = Object.fromEntries(
    parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value]),
  );

  return {
    day: Number(values.day),
    month: Number(values.month),
    year: Number(values.year),
  };
};

export const getDateForTimeZone = (date: Date, timeZone: string) => {
  const parts = getDatePartsInTimeZone(date, timeZone);
  return new Date(parts.year, parts.month - 1, parts.day);
};

export const getStartOfNextDayInTimeZone = (date: Date, timeZone: string) => {
  const parts = getDatePartsInTimeZone(date, timeZone);
  return new Date(parts.year, parts.month - 1, parts.day + 1);
};
